import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useChannelSearch, useChannels } from "@/hooks/api/use-channels";
import { useChannelSelection } from "@/hooks/use-channel-selection";
import { cn } from "@/lib/utils";

export function ChannelSelector() {
  const { selectedChannelType, setSelectedChannelType } = useChannelSelection();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch channels from the backend
  const { data: channelsData, isLoading: isLoadingChannels } = useChannels();
  const { data: searchResults, isLoading: isSearching } = useChannelSearch(
    searchQuery.length > 1 ? searchQuery : undefined,
  );

  // Use search results when searching, otherwise use all channels
  const channels =
    searchQuery.length > 1
      ? searchResults?.results || []
      : channelsData?.results || [];

  const selectedChannel = channels.find((c) => c.name === selectedChannelType);

  // Channel selection handler
  const handleChannelSelect = (channelName: string) => {
    setSelectedChannelType(channelName);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between gap-6 min-w-[300px] whitespace-nowrap overflow-hidden bg-white dark:bg-[hsl(195_30%_12%)]"
        >
          {selectedChannel ? selectedChannel.name : "Select channel..."}
          <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[360px]" align="start">
        <Command>
          <CommandInput
            placeholder="Search channels..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {(isLoadingChannels || isSearching) && (
              <div className="py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">
                  Loading channels...
                </p>
              </div>
            )}

            {!isLoadingChannels && !isSearching && channels.length === 0 && (
              <CommandEmpty>No channels found.</CommandEmpty>
            )}

            {!isLoadingChannels && !isSearching && channels.length > 0 && (
              <CommandGroup>
                {channels.map((channel) => (
                  <CommandItem
                    key={channel.name}
                    value={channel.name}
                    onSelect={() => handleChannelSelect(channel.name)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedChannelType === channel.name
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {channel.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
